import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/types/linguadeck";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

type TermDelimiterType = "tab" | "comma" | "custom";
type RowDelimiterType = "newline" | "semicolon" | "custom";

const TERM_DELIMITER_VALUES: Record<TermDelimiterType, string> = {
  tab: "\t",
  comma: ",",
  custom: "",
};

const ROW_DELIMITER_VALUES: Record<RowDelimiterType, string> = {
  newline: "\n",
  semicolon: ";",
  custom: "",
};

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  cards: Card[];
  deckTitle?: string;
}

export default function ExportModal({ open, onClose, cards, deckTitle }: ExportModalProps) {
  const { t } = useTranslation();
  const [termDelimiter, setTermDelimiter] = useState<TermDelimiterType>("tab");
  const [termCustom, setTermCustom] = useState("");
  const [rowDelimiter, setRowDelimiter] = useState<RowDelimiterType>("newline");
  const [rowCustom, setRowCustom] = useState("");
  const [isAlphabetical, setIsAlphabetical] = useState(false);

  const termSep = termDelimiter === "custom" ? termCustom : TERM_DELIMITER_VALUES[termDelimiter];
  const rowSep =
    rowDelimiter === "custom"
      ? rowCustom
      : ROW_DELIMITER_VALUES[rowDelimiter];

  const formattedText = useMemo(() => {
    const tSep = termSep || "\t";
    const rSep = rowSep || "\n";
    let list = cards.map((c) => ({ front: c.front, back: c.back }));
    if (isAlphabetical) {
      list = [...list].sort((a, b) =>
        (a.front || "").localeCompare(b.front || "", undefined, { sensitivity: "base" })
      );
    }
    return list
      .filter((c) => c.front || c.back)
      .map((c) => `${(c.front || "").trim()}${tSep}${(c.back || "").trim()}`)
      .join(rSep);
  }, [cards, termSep, rowSep, isAlphabetical]);

  const baseName = (deckTitle || "deck").replace(/[^\w\s-]/gi, "").replace(/\s+/g, "_");

  const handleCopy = () => {
    navigator.clipboard.writeText(formattedText);
    toast.success(t("exportModal.copied"));
  };

  const handleDownload = (format: "json" | "csv") => {
    if (!cards.length) return;
    if (format === "json") {
      const blob = new Blob(
        [JSON.stringify(cards.map((c) => ({ front: c.front, back: c.back, hint: c.hint })), null, 2)],
        { type: "application/json" }
      );
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${baseName}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } else {
      const csv = cards
        .map((c) => `"${(c.front || "").replace(/"/g, '""')}","${(c.back || "").replace(/"/g, '""')}"`)
        .join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${baseName}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    }
    toast.success(t("exportModal.downloaded"));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl"
        style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}
      >
        <DialogHeader>
          <DialogTitle>{t("exportModal.title")}</DialogTitle>
        </DialogHeader>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                {t("importModal.termDelimiter")}
              </label>
              <div className="flex flex-wrap gap-4">
                {(["tab", "comma", "custom"] as const).map((opt) => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="exportTermDelimiter"
                      checked={termDelimiter === opt}
                      onChange={() => setTermDelimiter(opt)}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {opt === "tab" && "Tab"}
                      {opt === "comma" && t("importModal.comma")}
                      {opt === "custom" && t("importModal.custom")}
                    </span>
                  </label>
                ))}
              </div>
              {termDelimiter === "custom" && (
                <input
                  type="text"
                  value={termCustom}
                  onChange={(e) => setTermCustom(e.target.value)}
                  placeholder={t("importModal.exampleDelimiter")}
                  className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--bg3))] text-sm"
                />
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                {t("importModal.rowDelimiter")}
              </label>
              <div className="flex flex-wrap gap-4">
                {(["newline", "semicolon", "custom"] as const).map((opt) => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="exportRowDelimiter"
                      checked={rowDelimiter === opt}
                      onChange={() => setRowDelimiter(opt)}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {opt === "newline" && t("importModal.newline")}
                      {opt === "semicolon" && t("importModal.semicolon")}
                      {opt === "custom" && t("importModal.custom")}
                    </span>
                  </label>
                ))}
              </div>
              {rowDelimiter === "custom" && (
                <input
                  type="text"
                  value={rowCustom}
                  onChange={(e) => setRowCustom(e.target.value)}
                  placeholder={t("importModal.exampleRowDelimiter")}
                  className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--bg3))] text-sm"
                />
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="exportAlphabetical"
              checked={isAlphabetical}
              onChange={(e) => setIsAlphabetical(e.target.checked)}
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <label
              htmlFor="exportAlphabetical"
              className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
            >
              {t("exportModal.alphabetical")}
            </label>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              {t("exportModal.preview")}
            </label>
            <textarea
              readOnly
              value={formattedText}
              className="w-full min-h-[160px] px-4 py-3 rounded-xl border border-gray-200 dark:border-[hsl(var(--border))] bg-gray-50 dark:bg-[hsl(var(--bg3))] text-gray-800 dark:text-[hsl(var(--foreground))] font-mono text-sm resize-none"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            />
          </div>
        </div>

        <DialogFooter style={{ marginTop: 16 }} className="flex-wrap gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-gray-200 dark:border-[hsl(var(--border))] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[hsl(var(--bg3))] text-sm font-medium transition-colors"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!formattedText}
            className="px-4 py-2 rounded-xl border border-purple-600 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-500/20 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("exportModal.copyText")}
          </button>
          <button
            type="button"
            onClick={() => handleDownload("csv")}
            disabled={!cards.length}
            className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
          >
            {t("exportModal.downloadCsv")}
          </button>
          <button
            type="button"
            onClick={() => handleDownload("json")}
            disabled={!cards.length}
            className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
          >
            {t("exportModal.downloadJson")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
